import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Loader2, Plus, Trash2, ArrowUp, ArrowDown, Image as ImageIcon, Type, Heading, X, Globe, Sparkles, Check, RotateCcw, Wand2 } from "lucide-react";
import { mediaUrl } from "@/mediaUrl";
import EmojiQuickPick from "@/EmojiQuickPick";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/** Studio-side editor for the long-form article that lives below the
 *  video on each public story page. Block types: heading, paragraph,
 *  image (with caption). Saving replaces the canonical English body and
 *  invalidates every cached translation — public GETs in non-English
 *  languages will re-translate on next read.
 */
export default function StoryBodyEditor({ video, onClose, onSaved }) {
  const [blocks, setBlocks] = useState([]);
  const [translatedLangs, setTranslatedLangs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(0);
  const [error, setError] = useState("");
  const [pretranslating, setPretranslating] = useState(false);
  const [pretranslateMsg, setPretranslateMsg] = useState("");
  // AI-polish state. `polishProposal` holds the suggested blocks while
  // the user decides — accepting it overwrites `blocks`; rejecting it
  // tosses it away. Keeping the original visible side-by-side avoids
  // the "did the AI silently rewrite my story?" problem.
  const [polishing, setPolishing] = useState(false);
  const [polishProposal, setPolishProposal] = useState(null);
  const [polishError, setPolishError] = useState("");
  // Auto-write state. Mirrors the polish review flow: the proposed blocks
  // are shown side-by-side with whatever's already in the editor and the
  // creator decides whether to replace their draft.
  const [autoWriting, setAutoWriting] = useState(false);
  const [autoWriteProposal, setAutoWriteProposal] = useState(null);
  const [autoWriteError, setAutoWriteError] = useState("");
  const [autoWriteUsed, setAutoWriteUsed] = useState({ transcript: false, scrape: false });
  const fileInputRef = useRef(null);
  const [pendingImageIndex, setPendingImageIndex] = useState(null);

  // Load the current body so the editor opens with what's already there.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/videos/${video.id}/story-body`);
        if (cancelled) return;
        setBlocks(r.data.blocks || []);
        setTranslatedLangs(r.data.translated_languages || []);
      } catch (_e) {
        if (!cancelled) setError("Couldn't load article. Try closing and reopening.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [video.id]);

  // Escape closes (without saving — the Save button is the explicit commit).
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const updateBlock = (i, patch) => {
    setBlocks((prev) => prev.map((b, idx) => (idx === i ? { ...b, ...patch } : b)));
  };
  const removeBlock = (i) => setBlocks((prev) => prev.filter((_, idx) => idx !== i));
  const moveBlock = (i, delta) => {
    setBlocks((prev) => {
      const next = [...prev];
      const j = i + delta;
      if (j < 0 || j >= next.length) return prev;
      [next[i], next[j]] = [next[j], next[i]];
      return next;
    });
  };
  const addBlock = (kind) => {
    if (kind === "image") {
      // Two-step: open the file picker first, attach the URL on success.
      setPendingImageIndex(blocks.length);
      fileInputRef.current?.click();
      return;
    }
    setBlocks((prev) => [...prev, { type: kind, text: "" }]);
  };

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) { setPendingImageIndex(null); return; }
    if (!/^image\/(jpeg|png|webp)$/.test(file.type)) {
      setError("Image must be JPG, PNG or WebP.");
      setPendingImageIndex(null);
      return;
    }
    setError("");
    setSaving(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const r = await axios.post(`${API}/videos/${video.id}/story-body/image`, form);
      setBlocks((prev) => [...prev, {
        type: "image", url: r.data.url, caption: "", alt: "",
      }]);
    } catch (err) {
      setError(err?.response?.data?.detail || "Image upload failed.");
    } finally {
      setSaving(false);
      setPendingImageIndex(null);
    }
  };

  const onSave = async () => {
    setError("");
    setSaving(true);
    try {
      await axios.put(`${API}/videos/${video.id}/story-body`, { blocks });
      setSavedAt(Date.now());
      // Saving replaces the canonical body — every cached translation is
      // dropped server-side, so reflect that locally too.
      setTranslatedLangs([]);
      setPretranslateMsg("");
      if (onSaved) onSaved();
    } catch (err) {
      setError(err?.response?.data?.detail || "Couldn't save article.");
    } finally {
      setSaving(false);
    }
  };

  const onPretranslate = async () => {
    // Saving first guarantees the server has the latest copy. We always
    // do this — it's a no-op if nothing changed since the last save.
    if (blocks.length === 0) {
      setPretranslateMsg("Add at least one heading or paragraph first.");
      return;
    }
    setError("");
    setPretranslating(true);
    setPretranslateMsg("");
    try {
      await axios.put(`${API}/videos/${video.id}/story-body`, { blocks });
      const r = await axios.post(`${API}/videos/${video.id}/story-body/translate-all`);
      const newCodes = r.data.translated || [];
      const cachedCodes = r.data.already_cached || [];
      // Combined list shown in the footer.
      setTranslatedLangs([...new Set([...cachedCodes, ...newCodes])].sort());
      if (newCodes.length === 0 && cachedCodes.length > 0) {
        setPretranslateMsg("All languages were already cached.");
      } else if (newCodes.length === 0) {
        setPretranslateMsg(r.data.reason || "Nothing to translate.");
      } else {
        setPretranslateMsg(
          `Translated into ${newCodes.length} ${newCodes.length === 1 ? "language" : "languages"}: ${newCodes.join(", ")}.`
        );
      }
    } catch (err) {
      setError(err?.response?.data?.detail || "Pre-translate failed — try again.");
    } finally {
      setPretranslating(false);
    }
  };

  const onPolish = async () => {
    if (blocks.length === 0) {
      setPolishError("Add at least one heading or paragraph first.");
      return;
    }
    setPolishError("");
    setPolishing(true);
    try {
      // Save the latest draft first so the AI works on what's on screen.
      await axios.put(`${API}/videos/${video.id}/story-body`, { blocks });
      const r = await axios.post(`${API}/videos/${video.id}/story-body/polish`);
      setPolishProposal(r.data.blocks || []);
    } catch (err) {
      setPolishError(err?.response?.data?.detail || "Polish failed — try again.");
    } finally {
      setPolishing(false);
    }
  };

  const acceptPolish = () => {
    if (!polishProposal) return;
    setBlocks(polishProposal);
    setPolishProposal(null);
  };
  const rejectPolish = () => setPolishProposal(null);

  const onAutoWrite = async () => {
    setAutoWriteError("");
    setAutoWriting(true);
    try {
      const r = await axios.post(`${API}/videos/${video.id}/story-body/auto-write`);
      setAutoWriteProposal(r.data.blocks || []);
      setAutoWriteUsed({
        transcript: !!r.data.used_transcript,
        scrape: !!r.data.used_scrape,
      });
    } catch (err) {
      setAutoWriteError(err?.response?.data?.detail || "Auto-write failed — try again.");
    } finally {
      setAutoWriting(false);
    }
  };

  const acceptAutoWrite = () => {
    if (!autoWriteProposal) return;
    setBlocks(autoWriteProposal);
    setAutoWriteProposal(null);
  };
  const rejectAutoWrite = () => setAutoWriteProposal(null);

  const fieldCls = "w-full px-3 py-2 rounded-lg bg-black/40 border border-white/10 text-sm focus:outline-none focus:border-[color:var(--accent)] transition-colors";
  const buttonGhost = "text-xs px-3 py-2 rounded-md border border-white/10 hover:border-[color:var(--accent)] hover:text-[color:var(--accent)] transition-colors inline-flex items-center justify-center gap-1.5";

  return (
    <div
      className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm overflow-y-auto p-4 md:p-8"
      onClick={onClose}
      data-testid="story-body-editor"
    >
      <div
        className="panel max-w-3xl w-full mx-auto my-12 md:my-6 p-6 md:p-8"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-label="Edit article"
      >
        <div className="flex items-start justify-between gap-4 mb-5">
          <div>
            <div className="text-[11px] uppercase tracking-[0.22em] text-[color:var(--accent)] mb-1.5">
              Story article
            </div>
            <div className="font-display text-2xl leading-tight">{video.title}</div>
            <div className="text-xs text-[color:var(--muted)] mt-1 leading-relaxed">
              Add a long-form write-up that appears below the video on the public page. Write in English — Storylo auto-translates to every viewer's language on demand.
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-[color:var(--muted)] hover:text-white transition-colors flex-shrink-0"
            data-testid="story-body-editor-close"
            aria-label="Close editor"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {loading ? (
          <div className="py-16 text-center">
            <Loader2 className="w-6 h-6 mx-auto animate-spin text-[color:var(--accent)]" />
          </div>
        ) : (
          <>
            {blocks.length === 0 && (
              <div className="text-center py-10 border border-dashed border-white/10 rounded-xl mb-5">
                <Type className="w-8 h-8 mx-auto text-[color:var(--muted)] opacity-50" />
                <div className="mt-3 text-sm text-[color:var(--muted)]">No article yet — add a heading or paragraph below to start.</div>
              </div>
            )}

            <div className="space-y-3">
              {blocks.map((b, i) => (
                <div key={i} className="group relative panel p-3 md:p-4 border border-white/5">
                  <div className="absolute right-2 top-2 flex gap-1 opacity-60 group-hover:opacity-100 transition-opacity">
                    <button onClick={() => moveBlock(i, -1)} disabled={i === 0} className="p-1.5 rounded hover:bg-white/10 disabled:opacity-30" title="Move up" aria-label="Move up">
                      <ArrowUp className="w-3.5 h-3.5" />
                    </button>
                    <button onClick={() => moveBlock(i, +1)} disabled={i === blocks.length - 1} className="p-1.5 rounded hover:bg-white/10 disabled:opacity-30" title="Move down" aria-label="Move down">
                      <ArrowDown className="w-3.5 h-3.5" />
                    </button>
                    <button onClick={() => removeBlock(i)} className="p-1.5 rounded hover:bg-red-500/20 hover:text-red-300" title="Delete" aria-label="Delete" data-testid={`story-body-block-delete-${i}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                  {b.type === "heading" && (
                    <input
                      value={b.text}
                      onChange={(e) => updateBlock(i, { text: e.target.value })}
                      placeholder="Section heading"
                      className={`${fieldCls} font-display text-lg`}
                      data-testid={`story-body-block-heading-${i}`}
                    />
                  )}
                  {b.type === "paragraph" && (
                    <textarea
                      value={b.text}
                      onChange={(e) => updateBlock(i, { text: e.target.value })}
                      placeholder="Write a paragraph…"
                      rows={4}
                      className={`${fieldCls} resize-y leading-relaxed`}
                      data-testid={`story-body-block-paragraph-${i}`}
                    />
                  )}
                  {b.type === "image" && (
                    <div className="space-y-2">
                      <div className="rounded-lg overflow-hidden border border-white/10 bg-black/40 max-h-[280px]">
                        <img src={mediaUrl(b.url)} alt={b.alt || ""} className="w-full h-auto block" />
                      </div>
                      <input
                        value={b.caption}
                        onChange={(e) => updateBlock(i, { caption: e.target.value })}
                        placeholder="Caption (optional)"
                        className={fieldCls}
                        data-testid={`story-body-block-caption-${i}`}
                      />
                      {/* Tap an emoji to append it to the end of the
                          caption (or remove the matching trailing one).
                          Keeps the caption a free-text field so the
                          creator can mix emojis with words however they
                          want (e.g. "Maria · 💚 living with anxiety"). */}
                      <EmojiQuickPick
                        value=""
                        onChange={(e) => {
                          const c = b.caption || "";
                          const next = c.endsWith(e) ? c.slice(0, -e.length).trimEnd() : (c ? `${c} ${e}` : e);
                          updateBlock(i, { caption: next });
                        }}
                        testId={`story-body-block-caption-emoji-${i}`}
                      />
                      <input
                        value={b.alt}
                        onChange={(e) => updateBlock(i, { alt: e.target.value })}
                        placeholder="Describe the image for accessibility (optional)"
                        className={`${fieldCls} text-xs text-[color:var(--muted)]`}
                      />
                    </div>
                  )}
                </div>
              ))}
            </div>

            <div className="flex flex-wrap gap-2 mt-5 pb-2 border-b border-white/5">
              <button onClick={() => addBlock("heading")} className={buttonGhost} data-testid="story-body-add-heading"><Heading className="w-3.5 h-3.5" /> Heading</button>
              <button onClick={() => addBlock("paragraph")} className={buttonGhost} data-testid="story-body-add-paragraph"><Type className="w-3.5 h-3.5" /> Paragraph</button>
              <button onClick={() => addBlock("image")} className={buttonGhost} data-testid="story-body-add-image">
                {pendingImageIndex !== null ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ImageIcon className="w-3.5 h-3.5" />}
                Image
              </button>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                onChange={onFile}
                className="hidden"
              />
            </div>

            {error && (
              <div className="text-xs text-red-300 p-2 rounded border border-red-500/30 bg-red-500/10 mt-4" data-testid="story-body-error">{error}</div>
            )}

            <div className="flex items-center justify-between mt-5 gap-3 flex-wrap">
              <div className="text-[10px] uppercase tracking-[0.2em] text-[color:var(--muted)]">
                {translatedLangs.length > 0 && savedAt === 0
                  ? `Cached translations: ${translatedLangs.join(", ")}`
                  : translatedLangs.length > 0
                  ? `Cached translations: ${translatedLangs.join(", ")}`
                  : savedAt
                  ? "Saved · translations refresh on next read"
                  : "Draft"}
              </div>
              <div className="flex gap-2 flex-wrap justify-end">
                <button
                  onClick={onAutoWrite}
                  disabled={autoWriting || polishing || saving || pretranslating}
                  className={`${buttonGhost} disabled:opacity-50`}
                  data-testid="story-body-auto-write"
                  title="Generate a 3-paragraph first draft from your video transcript and donate-link page"
                >
                  {autoWriting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Wand2 className="w-3.5 h-3.5" />}
                  {autoWriting ? "Writing…" : "Auto-write article"}
                </button>
                <button
                  onClick={onPolish}
                  disabled={polishing || saving || pretranslating || autoWriting || blocks.length === 0}
                  className={`${buttonGhost} disabled:opacity-50`}
                  data-testid="story-body-polish"
                  title="AI editor that fixes typos, tightens grammar, and breaks long sentences — without changing your meaning"
                >
                  {polishing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
                  {polishing ? "Polishing…" : "AI polish"}
                </button>
                <button
                  onClick={onPretranslate}
                  disabled={pretranslating || saving || polishing || blocks.length === 0}
                  className={`${buttonGhost} disabled:opacity-50`}
                  data-testid="story-body-pretranslate"
                  title="Translate the article into all 8 other languages now so the first foreign visitor doesn't wait"
                >
                  {pretranslating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Globe className="w-3.5 h-3.5" />}
                  {pretranslating ? "Translating…" : "Pre-translate now"}
                </button>
                <button onClick={onClose} className={buttonGhost} data-testid="story-body-cancel">
                  Close
                </button>
                <button
                  onClick={onSave}
                  disabled={saving}
                  className="text-sm px-4 py-2 rounded-md bg-[color:var(--accent)] text-black font-medium hover:opacity-90 transition-opacity inline-flex items-center gap-2 disabled:opacity-60"
                  data-testid="story-body-save"
                >
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                  {saving ? "Saving…" : "Save article"}
                </button>
              </div>
            </div>
            {pretranslateMsg && (
              <div className="text-[11px] text-[color:var(--accent)]/90 mt-2" data-testid="story-body-pretranslate-msg">
                {pretranslateMsg}
              </div>
            )}
            {polishError && (
              <div className="text-xs text-red-300 p-2 rounded border border-red-500/30 bg-red-500/10 mt-2" data-testid="story-body-polish-error">{polishError}</div>
            )}
            {autoWriteError && (
              <div className="text-xs text-red-300 p-2 rounded border border-red-500/30 bg-red-500/10 mt-2" data-testid="story-body-auto-write-error">{autoWriteError}</div>
            )}
          </>
        )}

        {polishProposal && (
          <PolishReview
            originalBlocks={blocks}
            proposalBlocks={polishProposal}
            onAccept={acceptPolish}
            onReject={rejectPolish}
          />
        )}

        {autoWriteProposal && (
          <AutoWriteReview
            originalBlocks={blocks}
            proposalBlocks={autoWriteProposal}
            usedTranscript={autoWriteUsed.transcript}
            usedScrape={autoWriteUsed.scrape}
            onAccept={acceptAutoWrite}
            onReject={rejectAutoWrite}
          />
        )}
      </div>
    </div>
  );
}

/* ----------------------------------------------------------------------- */
/* PolishReview — side-by-side diff between the user's draft and the AI's  */
/* polished proposal. Shown as an overlay so the user can scroll, compare, */
/* and explicitly Accept (replaces the draft) or Discard (keeps original). */
/* No silent rewrites. The AI never wins by default.                       */
/* ----------------------------------------------------------------------- */
function PolishReview({ originalBlocks, proposalBlocks, onAccept, onReject }) {
  // Pair blocks by index so the diff lines up. (Polish never adds/removes
  // blocks — it only edits text — so a 1:1 pairing is safe.)
  const pairs = originalBlocks.map((orig, i) => ({
    orig, proposed: proposalBlocks[i] || orig,
  }));
  const changedCount = pairs.filter(({ orig, proposed }) =>
    (orig.text || "") !== (proposed.text || "")
  ).length;
  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center p-4 md:p-8 bg-black/90 backdrop-blur-sm"
      onClick={onReject}
      data-testid="story-body-polish-review"
    >
      <div
        className="panel max-w-5xl w-full mx-auto my-12 md:my-6 p-6 md:p-7"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-label="Review AI polish"
      >
        <div className="flex items-start justify-between gap-4 mb-4">
          <div>
            <div className="text-[11px] uppercase tracking-[0.22em] text-[color:var(--accent)] mb-1.5 inline-flex items-center gap-1.5">
              <Sparkles className="w-3.5 h-3.5" /> AI polish · review
            </div>
            <div className="font-display text-xl leading-tight">
              {changedCount === 0
                ? "No changes suggested — your draft is clean."
                : `${changedCount} ${changedCount === 1 ? "block was" : "blocks were"} polished.`}
            </div>
            <div className="text-xs text-[color:var(--muted)] mt-1">
              Compare side-by-side. Nothing changes until you tap Accept.
            </div>
          </div>
          <button onClick={onReject} className="text-[color:var(--muted)] hover:text-white" aria-label="Discard">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="space-y-3">
          {pairs.map(({ orig, proposed }, i) => {
            const isText = orig.type === "heading" || orig.type === "paragraph";
            const changed = isText && (orig.text || "") !== (proposed.text || "");
            if (!isText) {
              return (
                <div key={i} className="text-[10px] uppercase tracking-[0.2em] text-[color:var(--muted)] py-2">
                  · image · unchanged ·
                </div>
              );
            }
            return (
              <div
                key={i}
                className={`grid grid-cols-1 md:grid-cols-2 gap-3 p-3 rounded-lg ${changed ? "bg-[color:var(--accent)]/5 border border-[color:var(--accent)]/20" : "bg-white/[.02] border border-white/5 opacity-60"}`}
              >
                <div>
                  <div className="text-[10px] uppercase tracking-[0.2em] text-[color:var(--muted)] mb-1.5">Your draft</div>
                  <div className={`text-sm leading-relaxed whitespace-pre-wrap ${orig.type === "heading" ? "font-display text-base" : ""}`}>
                    {orig.text}
                  </div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-[0.2em] text-[color:var(--accent)] mb-1.5">
                    {changed ? "Polished" : "Same as draft"}
                  </div>
                  <div className={`text-sm leading-relaxed whitespace-pre-wrap ${proposed.type === "heading" ? "font-display text-base" : ""}`}>
                    {proposed.text}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <div className="flex items-center justify-end gap-2 mt-5 pt-4 border-t border-white/5">
          <button
            onClick={onReject}
            className="text-xs px-3 py-2 rounded-md border border-white/10 hover:border-red-400 hover:text-red-300 transition-colors inline-flex items-center gap-1.5"
            data-testid="story-body-polish-reject"
          >
            <RotateCcw className="w-3.5 h-3.5" /> Discard suggestions
          </button>
          <button
            onClick={onAccept}
            disabled={changedCount === 0}
            className="text-sm px-4 py-2 rounded-md bg-[color:var(--accent)] text-black font-medium hover:opacity-90 transition-opacity inline-flex items-center gap-2 disabled:opacity-50"
            data-testid="story-body-polish-accept"
          >
            <Check className="w-4 h-4" /> {changedCount === 0 ? "Nothing to accept" : "Accept all"}
          </button>
        </div>
      </div>
    </div>
  );
}


/* ----------------------------------------------------------------------- */
/* AutoWriteReview — preview overlay for the AI-generated first draft. The  */
/* user sees the proposed heading + paragraphs (and what the AI was given   */
/* to work with) and explicitly chooses to use it or discard it. If there's */
/* an existing draft, we warn that accepting will replace it.               */
/* ----------------------------------------------------------------------- */
function AutoWriteReview({ originalBlocks, proposalBlocks, usedTranscript, usedScrape, onAccept, onReject }) {
  const hasDraft = (originalBlocks || []).length > 0;
  const sources = [];
  if (usedTranscript) sources.push("video transcript");
  if (usedScrape) sources.push("donate-link page");
  const sourceLabel = sources.length > 0 ? sources.join(" + ") : "available context";
  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center p-4 md:p-8 bg-black/90 backdrop-blur-sm"
      onClick={onReject}
      data-testid="story-body-auto-write-review"
    >
      <div
        className="panel max-w-3xl w-full mx-auto my-12 md:my-6 p-6 md:p-7"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-label="Review AI-generated article"
      >
        <div className="flex items-start justify-between gap-4 mb-4">
          <div>
            <div className="text-[11px] uppercase tracking-[0.22em] text-[color:var(--accent)] mb-1.5 inline-flex items-center gap-1.5">
              <Wand2 className="w-3.5 h-3.5" /> AI draft · review
            </div>
            <div className="font-display text-xl leading-tight">
              {(proposalBlocks || []).length === 0
                ? "Nothing was generated."
                : "First draft ready to review."}
            </div>
            <div className="text-xs text-[color:var(--muted)] mt-1">
              Drafted from your {sourceLabel}. Nothing changes until you tap Use this draft.
            </div>
          </div>
          <button onClick={onReject} className="text-[color:var(--muted)] hover:text-white" aria-label="Discard">
            <X className="w-5 h-5" />
          </button>
        </div>

        {hasDraft && (
          <div
            className="text-[11px] text-amber-200/90 bg-amber-500/10 border border-amber-500/30 rounded-md px-3 py-2 mb-4"
            data-testid="story-body-auto-write-replace-warning"
          >
            You already have an article in the editor. Accepting this draft will replace it.
          </div>
        )}

        <div className="space-y-3">
          {(proposalBlocks || []).map((b, i) => (
            <div key={i} className="p-3 rounded-lg bg-[color:var(--accent)]/5 border border-[color:var(--accent)]/20">
              <div className="text-[10px] uppercase tracking-[0.2em] text-[color:var(--accent)] mb-1.5">
                {b.type === "heading" ? "Heading" : "Paragraph"}
              </div>
              <div className={`text-sm leading-relaxed whitespace-pre-wrap ${b.type === "heading" ? "font-display text-base" : ""}`}>
                {b.text}
              </div>
            </div>
          ))}
        </div>

        <div className="flex items-center justify-end gap-2 mt-5 pt-4 border-t border-white/5">
          <button
            onClick={onReject}
            className="text-xs px-3 py-2 rounded-md border border-white/10 hover:border-red-400 hover:text-red-300 transition-colors inline-flex items-center gap-1.5"
            data-testid="story-body-auto-write-reject"
          >
            <RotateCcw className="w-3.5 h-3.5" /> Discard
          </button>
          <button
            onClick={onAccept}
            disabled={(proposalBlocks || []).length === 0}
            className="text-sm px-4 py-2 rounded-md bg-[color:var(--accent)] text-black font-medium hover:opacity-90 transition-opacity inline-flex items-center gap-2 disabled:opacity-50"
            data-testid="story-body-auto-write-accept"
          >
            <Check className="w-4 h-4" /> Use this draft
          </button>
        </div>
      </div>
    </div>
  );
}
